import { useState, useMemo, useCallback } from 'react';
import {
  Mail,
  Search,
  Clock,
  AlertTriangle,
  Archive,
  Forward,
  MessageSquare,
  HardDrive,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { SyncedUserMailbox, SyncedSharedMailbox, SyncCategory } from '../../types';
import { formatSyncTime } from '../../hooks/useClientSyncData';
import SharedMailboxList from './SharedMailboxList';

type SortColumn = 'displayName' | 'email' | 'mailboxType' | 'totalSizeMB' | 'itemCount' | 'archiveEnabled' | 'autoReplyEnabled' | null;
type SortDirection = 'asc' | 'desc' | null;
type SubTab = 'all' | 'shared';

function formatSize(mb: number | null): string {
  if (mb === null || mb === undefined) return '-';
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
}

function mailboxTypeBadge(type: string) {
  const lower = type.toLowerCase();
  if (lower === 'user' || lower === 'usermailbox') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">User</span>;
  }
  if (lower === 'shared' || lower === 'sharedmailbox') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300">Shared</span>;
  }
  if (lower === 'room' || lower === 'roommailbox') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">Room</span>;
  }
  if (lower === 'equipment' || lower === 'equipmentmailbox') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">Equipment</span>;
  }
  return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{type}</span>;
}

interface MailboxesTabProps {
  clientId: string;
  clientName: string;
  tenantId: string | null;
  userMailboxes?: SyncCategory<SyncedUserMailbox>;
  sharedMailboxes?: SyncCategory<SyncedSharedMailbox>;
  onRefreshNeeded?: () => void;
}

export default function MailboxesTab({
  clientId,
  clientName,
  tenantId,
  userMailboxes,
  sharedMailboxes,
  onRefreshNeeded,
}: MailboxesTabProps) {
  const [subTab, setSubTab] = useState<SubTab>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('displayName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = useCallback((column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  const mailboxes = userMailboxes?.data || [];
  const lastSynced = userMailboxes?.lastSynced;
  const syncStatus = userMailboxes?.status;
  const syncError = userMailboxes?.error;

  const sharedCount = sharedMailboxes?.data?.length || 0;
  const allCount = mailboxes.length;

  const filteredAndSorted = useMemo(() => {
    let result = mailboxes;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (mb) =>
          mb.displayName.toLowerCase().includes(term) ||
          mb.email.toLowerCase().includes(term) ||
          mb.mailboxType.toLowerCase().includes(term) ||
          (mb.forwardingAddress && mb.forwardingAddress.toLowerCase().includes(term))
      );
    }
    if (sortColumn && sortDirection) {
      const dir = sortDirection === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        switch (sortColumn) {
          case 'displayName':
            return dir * a.displayName.localeCompare(b.displayName);
          case 'email':
            return dir * a.email.localeCompare(b.email);
          case 'mailboxType':
            return dir * a.mailboxType.localeCompare(b.mailboxType);
          case 'totalSizeMB':
            return dir * ((a.totalSizeMB || 0) - (b.totalSizeMB || 0));
          case 'itemCount':
            return dir * ((a.itemCount || 0) - (b.itemCount || 0));
          case 'archiveEnabled':
            return dir * (Number(a.archiveEnabled) - Number(b.archiveEnabled));
          case 'autoReplyEnabled':
            return dir * (Number(a.autoReplyEnabled) - Number(b.autoReplyEnabled));
          default:
            return 0;
        }
      });
    }
    return result;
  }, [mailboxes, searchTerm, sortColumn, sortDirection]);

  const SortIndicator = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5" />;
  };

  const thClass = "px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground";

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="border-b border-border">
        <nav className="flex gap-6">
          <button
            onClick={() => setSubTab('all')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              subTab === 'all'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <HardDrive className="h-4 w-4 inline mr-1.5" />
            All Mailboxes
            {allCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300">
                {allCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setSubTab('shared')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              subTab === 'shared'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Mail className="h-4 w-4 inline mr-1.5" />
            Shared Mailboxes
            {sharedCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                {sharedCount}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* All Mailboxes sub-tab */}
      {subTab === 'all' && (
        <div className="space-y-4">
          {/* No tenant */}
          {!tenantId ? (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
              <p className="text-yellow-700 dark:text-yellow-300">
                No Microsoft tenant ID configured. Please link this client to a tenant to view mailboxes.
              </p>
            </div>
          ) : !userMailboxes || syncStatus === 'pending' ? (
            /* Not yet synced */
            <div className="text-center py-12 bg-muted rounded-lg">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                Data Not Yet Synced
              </h3>
              <p className="text-muted-foreground mb-2">
                User mailbox data has not been collected yet.
              </p>
              <p className="text-sm text-muted-foreground">
                Click "Sync Now" above to fetch mailbox data from Exchange Online.
              </p>
            </div>
          ) : syncStatus === 'failed' && mailboxes.length === 0 ? (
            /* Sync failed */
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-red-800 dark:text-red-200">Failed to sync mailbox data</h3>
                  <p className="text-red-700 dark:text-red-300 text-sm mt-1">
                    {syncError || 'An unknown error occurred'}
                  </p>
                  <p className="text-red-600 dark:text-red-400 text-xs mt-2">
                    Click "Sync Now" above to try again.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Search bar + staleness */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search mailboxes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                {lastSynced && (
                  <span className="text-xs text-muted-foreground">
                    Updated {formatSyncTime(lastSynced)}
                  </span>
                )}
              </div>

              {/* Empty state */}
              {mailboxes.length === 0 && syncStatus === 'success' && (
                <div className="text-center py-12 bg-muted rounded-lg">
                  <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No mailboxes found</h3>
                  <p className="text-muted-foreground">No mailbox data was returned from Exchange Online.</p>
                </div>
              )}

              {/* No search results */}
              {mailboxes.length > 0 && filteredAndSorted.length === 0 && searchTerm && (
                <div className="text-center py-12 bg-muted rounded-lg">
                  <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No mailboxes found</h3>
                  <p className="text-muted-foreground">Try adjusting your search terms.</p>
                </div>
              )}

              {/* Mailbox table */}
              {filteredAndSorted.length > 0 && (
                <div className="bg-card shadow rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-border">
                      <thead className="bg-muted">
                        <tr>
                          <th className={thClass} onClick={() => handleSort('displayName')}>
                            Name <SortIndicator column="displayName" />
                          </th>
                          <th className={`${thClass} hidden md:table-cell`} onClick={() => handleSort('email')}>
                            Email <SortIndicator column="email" />
                          </th>
                          <th className={thClass} onClick={() => handleSort('mailboxType')}>
                            Type <SortIndicator column="mailboxType" />
                          </th>
                          <th className={`${thClass} hidden lg:table-cell`} onClick={() => handleSort('totalSizeMB')}>
                            Size <SortIndicator column="totalSizeMB" />
                          </th>
                          <th className={`${thClass} hidden lg:table-cell`} onClick={() => handleSort('itemCount')}>
                            Items <SortIndicator column="itemCount" />
                          </th>
                          <th className={`${thClass} hidden xl:table-cell`} onClick={() => handleSort('archiveEnabled')}>
                            Archive <SortIndicator column="archiveEnabled" />
                          </th>
                          <th className={`${thClass} hidden xl:table-cell`}>
                            Forwarding
                          </th>
                          <th className={`${thClass} hidden xl:table-cell`} onClick={() => handleSort('autoReplyEnabled')}>
                            Auto-Reply <SortIndicator column="autoReplyEnabled" />
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filteredAndSorted.map((mb) => (
                          <tr key={mb.email} className="hover:bg-accent transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                  <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="ml-3">
                                  <div className="text-sm font-medium text-foreground">{mb.displayName}</div>
                                  <div className="text-xs text-muted-foreground md:hidden">{mb.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                              <span className="text-sm text-foreground">{mb.email}</span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {mailboxTypeBadge(mb.mailboxType)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                              <span className="text-sm text-foreground">{formatSize(mb.totalSizeMB)}</span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                              <span className="text-sm text-foreground">
                                {mb.itemCount !== null && mb.itemCount !== undefined
                                  ? mb.itemCount.toLocaleString()
                                  : '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap hidden xl:table-cell">
                              {mb.archiveEnabled ? (
                                <span className="inline-flex items-center gap-1">
                                  <Archive className="h-3.5 w-3.5 text-green-500" />
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                                    Enabled
                                  </span>
                                  {mb.archiveSizeMB !== null && mb.archiveSizeMB !== undefined && mb.archiveSizeMB > 0 && (
                                    <span className="text-xs text-muted-foreground ml-1">
                                      ({formatSize(mb.archiveSizeMB)})
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                  Disabled
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap hidden xl:table-cell">
                              {mb.forwardingAddress ? (
                                <span className="inline-flex items-center gap-1 text-sm text-foreground">
                                  <Forward className="h-3.5 w-3.5 text-amber-500" />
                                  <span className="truncate max-w-[180px]" title={mb.forwardingAddress}>
                                    {mb.forwardingAddress}
                                  </span>
                                  {mb.deliverAndForward && (
                                    <span className="text-xs text-muted-foreground">(+deliver)</span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap hidden xl:table-cell">
                              {mb.autoReplyEnabled ? (
                                <span className="inline-flex items-center gap-1">
                                  <MessageSquare className="h-3.5 w-3.5 text-green-500" />
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                                    On
                                  </span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                  Off
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Count */}
              {filteredAndSorted.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Showing {filteredAndSorted.length} of {mailboxes.length} mailboxes
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Shared Mailboxes sub-tab */}
      {subTab === 'shared' && (
        <SharedMailboxList
          clientId={clientId}
          clientName={clientName}
          tenantId={tenantId}
          syncData={sharedMailboxes}
          onRefreshNeeded={onRefreshNeeded}
        />
      )}
    </div>
  );
}
